import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Moment } from "moment";

import EntityChart from "../entityChart";
import ReportListView from "../reportListView";
import ContentDiscoveryMatrix from "../contentDiscoveryMatrix";
import AssetCard from "../assetCard";
import AssetListItem from "../assetListItem";
import { UserCard } from "../userCard";
import { UserListItem } from "../userListItem";
import { SharesCard } from "../sharesCard";
import { UserGroupCard } from "../userGroupCard";
import { UserGroupListItem } from "../userGroupListItem";
import { CollectionCard } from "../collectionCard";
import { CollectionListItem } from "../collectionListItem";
import { AssetCardList } from "../assetCardList";
import { SearchQueryCard } from "../searchQueryCard";
import { SearchQueryListItem } from "../searchQueryListItem";
import { SearchTagCard } from "../searchTagCard";
import { SearchTagsListItem } from "../searchTagsListItem";
import { SharesListItem } from "../sharesListItem";
import {
  fetchDiscoveryAssetViewsGraphReport,
  fetchDiscoveryAssetsGraphReport,
  fetchDiscoveryAssetsShared,
  fetchDiscoveryCollectionActivityGraphReport,
  fetchDiscoveryCollections,
  fetchDiscoveryCollectionsShared,
  fetchDiscoveryDealrooms,
  fetchDiscoveryDealRoomsShared,
  fetchDiscoveryPopularAssets,
  fetchDiscoverySearchQueries,
  fetchDiscoverySearchTags,
  fetchDiscoveryShareGraphReport,
  fetchDiscoveryTopShares,
  fetchDiscoveryTopUserGroups,
  fetchDiscoveryTopUsers,
} from "src/redux/actions/reports";
import {
  POPULAR_ASSET_SCHEMA,
  TOP_USERS_SCHEMA,
  SHARES_SCHEMA,
  TOP_USER_GROUPS_SCHEMA,
  COLLECTIONS_SCHEMA,
  DEAL_ROOMS_SCHEMA,
  ASSETS_SHARED_SCHEMA,
  SEARCH_QUERIES_SCHEMA,
  SEARCH_TAGS_SCHEMA,
  DISCOVERY_POPULAR_ASSET_SCHEMA,
} from "src/constants/reports";

import { useReportsAdoption } from "src/redux/actions/adoption/reports";

import userRoleService from "src/utils/userRoles.service";

import _ from "lodash";
import styles from "./discovery.module.scss";
import { DiscoveryTables, ReportTabs } from "src/types/reports";
import { DealRoomCard } from "../dealRoomCard";
import { DealRoomListItem } from "../dealRoomListItem";


interface DiscoveryTabProps {
  reportDate?: ReportDate;
  filters?: any;
  interval?: string;
}
interface ReportDate {
  startDate: Moment;
  endDate: Moment;
  type?: string;
}

const DiscoveryTab: React.FC<DiscoveryTabProps> = ({
  reportDate,
  filters,
  interval,
}) => {
  const dispatch = useDispatch();

  const { trackReportsToggledView } = useReportsAdoption();

  const { discovery } = useSelector((state: any) => ({
    discovery: _.get(state, "reports.discovery", {}),
  }));
  
  const {
    popularAssets,
    assetsShared,
    topUsers,
    topSharers,
    topUserGroups,
    collectionsCreated,
    collectionsShared,
    searchQueries,
    searchTags,
    assetsGraph,
    assetViewsGraph,
    collectionActivityGraph,
    sharesGraph,
    dealRoomsCreated,
    dealRoomsShared
  } = discovery;

  const validFilters = useMemo(() => {
    if (!filters || !Array.isArray(filters)) return [];

    return filters.filter((filter) => {
      const { property, operator, value } = filter || {};
      return (
        property?.type &&
        operator &&
        Array.isArray(value) &&
        value.length > 0
      );
    });
  }, [filters]);

  const baseFilters = useMemo(() => {
    if (!reportDate || !reportDate.startDate || !reportDate.endDate)
      return null;

    const formattedStartDate = reportDate.startDate.format("YYYY-MM-DD");
    const formattedEndDate = reportDate.endDate.format("YYYY-MM-DD");

    return {
      startDate: `${formattedStartDate}T00:00:00+00:00`,
      endDate: `${formattedEndDate}T23:59:59+00:00`,
      filters: filters || [],
      sortBy: "views",
      userType: "all",
      interval: interval || "day",
    };
  }, [reportDate, validFilters, interval]);

  const handleLoadMore = (assetType) => {

    switch (assetType) {
      case "popular":
        if (popularAssets.pagination.currentPage < popularAssets.pagination.totalPages) {
          const request = {
            page: popularAssets.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchDiscoveryPopularAssets(request));
        }
        break;
      case "shared":
        if (assetsShared.pagination.currentPage < assetsShared.pagination.totalPages) {
          const request = {
            page: assetsShared.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchDiscoveryAssetsShared(request));
        }
        break;
      case "topUsers":
        if (topUsers.pagination.currentPage < topUsers.pagination.totalPages) {
          const request = {
            page: topUsers.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchDiscoveryTopUsers(request));
        }
        break;
      case "topSharers":
        if (topSharers.pagination.currentPage < topSharers.pagination.totalPages) {
          const request = {
            page: topSharers.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchDiscoveryTopShares(request));
        }
        break;
      case "topUserGroups":
        if (topUserGroups.pagination.currentPage < topUserGroups.pagination.totalPages) {
          const request = {
            page: topUserGroups.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchDiscoveryTopUserGroups(request));
        }
        break;
      case "collectionsCreated":
        if (collectionsCreated.pagination.currentPage < collectionsCreated.pagination.totalPages) {
          const request = {
            page: collectionsCreated.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchDiscoveryCollections(request));
        }
        break;
      case "collectionsShared":
        if (collectionsShared.pagination.currentPage < collectionsShared.pagination.totalPages) {
          const request = {
            page: collectionsShared.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchDiscoveryCollectionsShared(request));
        }
        break;
      case "searchQueries":
        if (searchQueries.pagination.currentPage < searchQueries.pagination.totalPages) {
          const request = {
            page: searchQueries.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchDiscoverySearchQueries(request));
        }
        break;
      case "searchTags":
        if (searchTags.pagination.currentPage < searchTags.pagination.totalPages) {
          const request = {
            page: searchTags.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchDiscoverySearchTags(request));
        }
        break;
      case "dealRoomsCreated":
        if (dealRoomsCreated?.pagination.currentPage < dealRoomsCreated.pagination.totalPages) {
          const request = {
            page: dealRoomsCreated?.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchDiscoveryDealrooms(request));
        }
        break;
      case "dealRoomsShared":
        if (dealRoomsShared.pagination.currentPage < dealRoomsShared.pagination.totalPages) {
          const request = {
            page: dealRoomsShared.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchDiscoveryDealRoomsShared(request));
        }
        break;
      default:
        return;
    }
  };

  const handleViewMore = (assetType) => {
    switch (assetType) {
      case "popular":
        dispatch(
          fetchDiscoveryPopularAssets({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;

      case "shared":
        dispatch(
          fetchDiscoveryAssetsShared({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;

      case "topUsers":
        dispatch(
          fetchDiscoveryTopUsers({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;

      case "topSharers":
        dispatch(
          fetchDiscoveryTopShares({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;

      case "topUserGroups":
        dispatch(
          fetchDiscoveryTopUserGroups({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;

      case "collectionsCreated":
        dispatch(
          fetchDiscoveryCollections({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;

      case "collectionsShared":
        dispatch(
          fetchDiscoveryCollectionsShared({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;

      case "searchQueries":
        dispatch(
          fetchDiscoverySearchQueries({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;

      case "searchTags":
        dispatch(
          fetchDiscoverySearchTags({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;
      case "dealRoomsCreated":
        dispatch(
          fetchDiscoveryDealrooms({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;
      case "dealRoomsShared":
        dispatch(
          fetchDiscoveryDealRoomsShared({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;
      default:
        return;
    }
  };

  useEffect(() => {
    if (!baseFilters?.startDate || !baseFilters?.endDate) return;

    trackReportsToggledView({ tabType: _.capitalize(ReportTabs.discovery) });

    dispatch(fetchDiscoveryPopularAssets({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchDiscoveryAssetsShared({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchDiscoveryTopUsers({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchDiscoveryTopShares({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchDiscoveryTopUserGroups({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchDiscoveryCollections({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchDiscoveryCollectionsShared({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchDiscoverySearchQueries({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchDiscoverySearchTags({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchDiscoveryAssetViewsGraphReport({ filter: baseFilters }));
    dispatch(fetchDiscoveryAssetsGraphReport({ filter: baseFilters }));
    dispatch(fetchDiscoveryCollectionActivityGraphReport({ filter: baseFilters }));
    dispatch(fetchDiscoveryShareGraphReport({ filter: baseFilters }));
    dispatch(fetchDiscoveryDealrooms({ page: 0, limit: 5, filter: baseFilters }))
    dispatch(fetchDiscoveryDealRoomsShared({ page: 0, limit: 5, filter: baseFilters }));
  },[JSON.stringify(baseFilters)]);

  return (
    <div className={styles.cardsContainer}>
      <EntityChart
        title={`# of Asset Views (${assetViewsGraph?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="Monitor daily internal asset views to access engagement levels"
        data={assetViewsGraph?.item}
        legend={{ key: "Date", value: "Asset Views" }}
        containerClassName={styles.chartContainer}
        loading={assetViewsGraph.loading}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.assetViews}
      />
      <ContentDiscoveryMatrix  tabName={ReportTabs.discovery} tableName={DiscoveryTables.discoveryMatrix} reportDate={reportDate} filters={validFilters} interval={interval} />
      <EntityChart
        title={`# of Assets (${assetsGraph?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="Track the number of unique assets added to stream each day"
        data={assetsGraph?.item}
        loading={assetsGraph.loading}
        legend={{ key: "Date", value: "Assets" }}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.assets}
      />
      {userRoleService.canAccessCollection() && (
      <EntityChart
        title={`Collection Activity (${collectionActivityGraph?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="Count of unique collections that were updated on each day including edit and create activities"
        data={collectionActivityGraph?.item}
        legend={{ key: "Date", value: "Collections" }}
        loading={collectionActivityGraph.loading}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.collectionActivity}
      />
      )}
      { userRoleService.canShare() && (
      <EntityChart
        title={`Conversation Activity (${sharesGraph?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="View the count of shares made , offering insights into content distribution trends"
        data={sharesGraph?.item}
        legend={{ key: "Date", value: "Shares" }}
        loading={sharesGraph.loading}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.conversationActivity}
      />
      )}

      <ReportListView
        title={`What's popular with your team? (${popularAssets.pagination.totalRecordsCount})`}
        description="Assets ranked by top-views internally along with their engagement metrics"
        canDownload={_.get(popularAssets, 'pagination.totalRecordsCount', 0) <= 5}
        reportDate={reportDate}
        filters={validFilters}
        downloadConfig={{
          name: "discovery_assets",
          schema: DISCOVERY_POPULAR_ASSET_SCHEMA,
          fileName: "DiscoveryPopularAssets",
          title: "Popular Assets Report",
          reportType: "popularAssets",
        }}
        loading={popularAssets.loading}
        items={popularAssets.items}
        loadMore={() => handleLoadMore("popular")}
        viewMore={() => handleViewMore("popular")}
        pagination={popularAssets.pagination}
        renderItem={({ item }) => <AssetCard item={item} />}
        renderListItem={({ item, columnCount }) => (
          <AssetListItem item={item} columnCount={columnCount} />
        )}
        columns={["Views", "Avg View Time", "# of shares"]}
        firstColumn="Asset"
        tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.popularTeam}
      />

      <ReportListView
        title={`Who are your top users? (${topUsers.pagination.totalRecordsCount})`}
        description="Top users ranked based on asset views along with their engagement metrics"
        canDownload={_.get(topUsers, 'pagination.totalRecordsCount', 0) <= 5}
        reportDate={reportDate}
        filters={validFilters}
        downloadConfig={{
          name: "discovery_users",
          schema: TOP_USERS_SCHEMA,
          fileName: "DiscoveryTopUsers",
          title: "Top Users Report",
          reportType: "topUsers",
        }}
        loading={topUsers.loading}
        items={topUsers.items}
        loadMore={() => handleLoadMore("topUsers")}
        viewMore={() => handleViewMore("topUsers")}
        pagination={topUsers.pagination}
        renderItem={({ item }) => (
          <UserCard item={item} />
        )}
        renderListItem={({ item, columnCount }) => (
          <UserListItem item={item} columnCount={columnCount} />
        )}
        columns={["Views", "Avg View Time", "# of shares"]}
        firstColumn="Top Users"
        tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.topUsers}
      />

      { userRoleService.canShare() && (
      <ReportListView
        title={`Who are your top sharers ? (${topSharers.pagination.totalRecordsCount})`}
        description="Top sharers ranked based on their sharing activity"
        reportDate={reportDate}
        filters={validFilters}
        canDownload={_.get(topSharers, 'pagination.totalRecordsCount', 0) <= 5}
        downloadConfig={{
          name: "discovery_sharers",
          schema: SHARES_SCHEMA,
          fileName: "DiscoveryTopShares",
          title: "Top Shares Report",
          reportType: "topShares",
        }}
        loading={topSharers.loading}
        items={topSharers.items}
        loadMore={() => handleLoadMore("topSharers")}
        viewMore={() => handleViewMore("topSharers")}
        pagination={topSharers.pagination}
        renderItem={({ item }) => (
          <SharesCard item={item} />
        )}
        renderListItem={({ item, columnCount }) => (
          <SharesListItem item={item} columnCount={columnCount} />
        )}
        columns={["# of shares", "# of assets", "# of recipients"]}
        firstColumn="Top Shares"
         tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.topSharers}
      />
      )}

      <ReportListView
        title={`Who are your top user groups ? (${topUserGroups.pagination.totalRecordsCount})`}
        description="User groups with the highest cummulative asset , including share count , view count , and average view count"
        canDownload={_.get(topUserGroups, 'pagination.totalRecordsCount', 0) <= 5}
        reportDate={reportDate}
        filters={validFilters}
        downloadConfig={{
          name: "discovery_user_groups",
          schema: TOP_USER_GROUPS_SCHEMA,
          fileName: "DiscoveryTopUserGroups",
          title: "Top User Groups Report",
          reportType: "topUserGroups",
        }}
        loading={topUserGroups.loading}
        items={topUserGroups.items}
        loadMore={() => handleLoadMore("topUserGroups")}
        viewMore={() => handleViewMore("topUserGroups")}
        pagination={topUserGroups.pagination}
        renderItem={({ item }) => (
          <UserGroupCard item={item} />
        )}
        renderListItem={({ item, columnCount }) => (
          <UserGroupListItem item={item} columnCount={columnCount} />
        )}
        columns={["Views", "Avg View Time", "# of shares"]}
        firstColumn="Top User Groups"
        tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.topUserGroups}
      />

      {userRoleService.canAccessCollection() && (
      <ReportListView
        title={`Collections Created (${collectionsCreated.pagination.totalRecordsCount})`}
        description="All collections created with details on the creator, asset count, shares and views sorted by recency"
        canDownload={_.get(collectionsCreated, 'pagination.totalRecordsCount', 0) <= 5}
        downloadConfig={{
          name: "discovery_collections",
          schema: COLLECTIONS_SCHEMA,
          fileName: "DiscoveryCollections",
          title: "Collections Created Report",
          reportType: "collectionsCreated",
        }}
        reportDate={reportDate}
        filters={validFilters}
        loading={collectionsCreated.loading}
        items={collectionsCreated.items}
        loadMore={() => handleLoadMore("collectionsCreated")}
        viewMore={() => handleViewMore("collectionsCreated")}
        pagination={collectionsCreated.pagination}
        renderItem={({ item }) => (
          <CollectionCard item={item} />
        )}
        renderListItem={({ item, columnCount }) => (
          <CollectionListItem item={item} columnCount={columnCount} />
        )}
        columns={[
          "Created By",
          "Created On",
          "Last Updated",
          "Shares",
          "Views",
        ]}
        firstColumn="Collections Created"
         tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.collectionsCreated}
      />
      )}

      {userRoleService.canAccessCollection() && userRoleService.canShare() && (
      <ReportListView
        title={`Collections shared (${collectionsShared.pagination.totalRecordsCount})`}
        description="All collections shared with details on the creator, asset count, shares and views sorted by most shared collections"
        canDownload={_.get(collectionsShared, 'pagination.totalRecordsCount', 0) <= 5}
        downloadConfig={{
          name: "discovery_collections_shared",
          schema: COLLECTIONS_SCHEMA,
          fileName: "DiscoveryCollectionsShared",
          title: "Collections Shared Report",
          reportType: "collectionsShared",
        }}
        loading={collectionsShared.loading}
        items={collectionsShared.items}
        loadMore={() => handleLoadMore("collectionsShared")}
        viewMore={() => handleViewMore("collectionsShared")}
        reportDate={reportDate}
        filters={validFilters}
        pagination={collectionsShared.pagination}
        renderItem={({ item }) => (
          <CollectionCard item={item} />
        )}
        renderListItem={({ item, columnCount }) => (
          <CollectionListItem item={item} columnCount={columnCount} />
        )}
        columns={[
          "Created By",
          "Created On",
          "Last Updated",
          "Shares",
          "Views",
        ]}
        firstColumn="Collections Shared"
        tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.collectionsShared}
      />
      )}
      
      {userRoleService.canAccessDealRoom() && userRoleService.canShare() && (
      <ReportListView
        title={`Deal Rooms created (${dealRoomsCreated?.pagination?.totalRecordsCount})`}
        description="All deal rooms created with details on the creator, asset count, stakeholders and views sorted by recency"
        canDownload={_.get(dealRoomsCreated, 'pagination.totalRecordsCount', 0) <= 5}
        downloadConfig={{
          name: "discovery_deal_rooms",
          schema: DEAL_ROOMS_SCHEMA,
          fileName: "DiscoveryDealRooms",
          title: "Deal rooms Created Report",
          reportType: "dealRoomCreated",
        }}
        reportDate={reportDate}
        filters={validFilters}
        loading={dealRoomsCreated?.loading}
        items={dealRoomsCreated?.items}
        loadMore={() => handleLoadMore("dealRoomsCreated")}
        viewMore={() => handleViewMore("dealRoomsCreated")}
        pagination={dealRoomsCreated?.pagination}
        renderItem={({ item }) => (
          <DealRoomCard item={item} />
        )}
        renderListItem={({ item, columnCount }) => (
          <DealRoomListItem item={item} columnCount={columnCount} />
        )}
        columns={[
          "Created By",
          "Created On",
          "Last Updated",
          "Views",
          "Stakeholders",
          "Interactions"
        ]}
        firstColumn="Deal rooms Created"
        tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.dealRoomsCreated}
        />
      )}
      
      { userRoleService.canAccessDealRoom() && userRoleService.canShare() && (
      <ReportListView
        title={`Deal Rooms shared (${dealRoomsShared.pagination.totalRecordsCount})`}
        description="All deal rooms shared with details on the creator, asset count, stakeholders and views sorted by most invited stakeholders"
        canDownload={_.get(dealRoomsShared, 'pagination.totalRecordsCount', 0) <= 5}
        downloadConfig={{
          name: "discovery_deal_rooms_shared",
          schema: DEAL_ROOMS_SCHEMA,
          fileName: "DiscoveryDealRoomsShared",
          title: "Deal rooms Shared Report",
          reportType: "dealRoomsShared",
        }}
        loading={dealRoomsShared.loading}
        items={dealRoomsShared.items}
        loadMore={() => handleLoadMore("dealRoomsShared")}
        viewMore={() => handleViewMore("dealRoomsShared")}
        reportDate={reportDate}
        filters={validFilters}
        pagination={dealRoomsShared.pagination}
        renderItem={({ item }) => (
          <DealRoomCard item={item} />
        )}
        renderListItem={({ item, columnCount }) => (
          <DealRoomListItem item={item} columnCount={columnCount} />
        )}
        columns={[
          "Created By",
          "Created On",
          "Last Updated",
          "Views",
          "Stakeholders",
          "Interactions"
        ]}
        firstColumn="Deal rooms Shared"
        tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.dealRoomsShared}
      />
      )}

      {userRoleService.canShare() && (
      <ReportListView
        title={`Frequently Shared Assets (${assetsShared.pagination.totalRecordsCount})`}
        description="Most shared assets with insights into view count, average view time and share count"
        canDownload={_.get(assetsShared, 'pagination.totalRecordsCount', 0) <= 5}
        downloadConfig={{
          name: "discovery_assets_shared",
          schema: ASSETS_SHARED_SCHEMA,
          fileName: "DiscoveryAssetsShared",
          title: "Assets Shared Report",
          reportType: "assetsShared",
        }}
        reportDate={reportDate}
        filters={validFilters}
        loading={assetsShared.loading}
        items={assetsShared.items}
        loadMore={() => handleLoadMore("shared")}
        viewMore={() => handleViewMore("shared")}
        pagination={assetsShared.pagination}
        renderItem={({ item }) => <AssetCard item={item} />}
        renderListItem={({ item, columnCount }) => (
          <AssetCardList item={item} columnCount={columnCount} />
        )}
        columns={[
          "Author",
          "Views",
          "Avg View Time",
          "Downloads",
          "# of shares",
        ]}
        firstColumn="Assets Shared"
        tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.sharedAssets}
      />
      )}

      <ReportListView
        title={`What are the most searched queries ? (${searchQueries.pagination.totalRecordsCount})`}
        description="Discover the most frequently searched queries by users to understand discovery trends"
        canDownload={_.get(searchQueries, 'pagination.totalRecordsCount', 0) <= 5}
        downloadConfig={{
          name: "discovery_search_queries",
          schema: SEARCH_QUERIES_SCHEMA,
          fileName: "DiscoverySearchQueries",
          title: "Search Queries Report",
          reportType: "searchQueries",
        }}
        reportDate={reportDate}
        filters={validFilters}
        loading={searchQueries.loading}
        items={searchQueries.items.map((item, index) => ({
          ...item,
          manualIndex: index + 1,
        }))}
        loadMore={() => handleLoadMore("searchQueries")}
        viewMore={() => handleViewMore("searchQueries")}
        isSearchGrid={true}
        isSearchQueries={true}
        pagination={searchQueries.pagination}
        renderItem={({ item }) => <SearchQueryCard item={item} />}
        renderListItem={({ item, columnCount }) => (
          <SearchQueryListItem item={item} columnCount={columnCount} />
        )}
        columns={["Searches"]}
        firstColumn="Search Queries"
        tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.searchedQueries}
      />

      <ReportListView
        title={`What are the most used tags ? (${searchTags.pagination.totalRecordsCount})`}
        description="Popular tags most frequently used to categorize assets and retrieve them during search"
        canDownload={_.get(searchTags, 'pagination.totalRecordsCount', 0) <= 5}
        downloadConfig={{
          name: "discovery_search_tags",
          schema: SEARCH_TAGS_SCHEMA,
          fileName: "DiscoverySearchTags",
          title: "Search Tags Report",
          reportType: "searchTags",
        }}
        reportDate={reportDate}
        filters={validFilters}
        loading={searchTags.loading}
        items={searchTags.items.map((item, index) => ({
          ...item,
          manualIndex: index + 1,
        }))}
        loadMore={() => handleLoadMore("searchTags")}
        viewMore={() => handleViewMore("searchTags")}
        isSearchGrid={true}
        pagination={searchTags.pagination}
        renderItem={({ item }) => <SearchTagCard item={item} />}
        renderListItem={({ item, columnCount }) => (
          <SearchTagsListItem item={item} columnCount={columnCount} />
        )}
        columns={["# of Assets", "# of Searches"]}
        firstColumn="Tags"
        tabName={ReportTabs.discovery}
        tableName={DiscoveryTables.usedTags}
      />
    </div>
  );
};

export default DiscoveryTab;
